/**
 * ELF-CIRCLE — Competitive Positioning (design artboard `#10d`, The Full Case).
 *
 * The combined variant: the thesis statement and stat row (10a), the
 * head-to-head "tug of war" rows where the ELF needle slides left past the
 * typical-agency one on hover (10b), and the four pillars as a card stack that
 * fans out over a film still (10c).
 *
 * Static page — imagery is bundled rather than an editable CMS slot.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import ElfShutter from '../ElfShutter';
import billboardArt from '../../../../../assets/elf/why-billboard.jpg';
import voilacodeArt from '../../../../../assets/elf/why-billboard-voilacode.jpg';
import culturalArt from '../../../../../assets/elf/pillar-cultural.jpg';
import oneRoofArt from '../../../../../assets/elf/pillar-one-roof.jpg';
import ownedIpArt from '../../../../../assets/elf/pillar-owned-ip.jpg';
import recognitionArt from '../../../../../assets/elf/pillar-recognition.jpg';

const STATS = [
  ['35', 'Years built from culture'],
  ['3', 'Categories — E · L · F'],
  ['1', 'Roof: Marketing + Web + GEO'],
];

/** Left needle is the typical agency, right needle is ELF-CIRCLE. */
const TUG: [string, string, string][] = [
  ['Multicultural expertise', 'A division, looped in', 'The category itself — 35 years'],
  ['Owned platforms', 'Client case studies only', 'Made In New Jersey — proprietary'],
  ['Insight layer', 'Founder instinct, undocumented', 'Named Cultural Intelligence practice'],
  ['Vendor structure', '5–6 agencies, client coordinates', 'One roof: Marketing + Web + GEO'],
  ['Recognition', 'General-market circuits', 'ADCOLOR, Cannes, Effie, The One Show'],
];

const PILLARS = [
  { num: '01', name: 'Owned IP', art: ownedIpArt, more: 'Made In New Jersey — a statewide platform no client engagement can replicate.' },
  { num: '02', name: 'Cultural Intelligence', art: culturalArt, more: 'A named practice above Marketing & Web Development — instinct, formalized.' },
  { num: '03', name: 'One Roof', art: oneRoofArt, more: 'Marketing + Web Development + GEO — one team, one point of accountability.' },
  { num: '04', name: 'Recognition', art: recognitionArt, more: 'ADCOLOR, Cannes Music Lions, Effie, The One Show — pursued deliberately.' },
];

export const WhyElfCircle: React.FC = () => {
  const consult = useConsult();
  const scopeRef = useRef<HTMLDivElement>(null);
  /** Touch-width deck: a tap fans it into a column (see the ≤820px CSS). */
  const [fanned, setFanned] = useState(false);
  /** The pillar last pointed at or tapped — its picture backs the band. */
  const [pillar, setPillar] = useState(0);
  /** Tap equivalent of hover for the comparison rows. */
  const [pulled, setPulled] = useState<string | null>(null);

  /* --- staggered scroll reveal for the comparison rows --------------------- */
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return;
    const rows = Array.from(scope.querySelectorAll<HTMLElement>('.elf-rise'));
    if (!rows.length) return;

    // No observer (a very old browser) means nothing may ever reveal, so show
    // everything at once. Reduced motion keeps the reveal — CSS drops the slide
    // and leaves the fade.
    if (typeof IntersectionObserver === 'undefined') {
      rows.forEach((el) => el.classList.add('in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          io.unobserve(entry.target); // one-shot
        });
      },
      { threshold: 0.15 },
    );
    rows.forEach((el, i) => {
      el.style.transitionDelay = `${Math.min(i, 6) * 45}ms`;
      io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  /* --- scroll progress for the comparison rows ------------------------------
   * Rows the reader has scrolled down to are marked reached and light up; rows
   * still below stay blurred (phone styles in elf-pages.css). The last row
   * reached is the current one and takes the full highlight. Scrolling back up
   * blurs the rows below again, so the highlight always tracks where the reader
   * is. Data attributes, not classes: the reveal above adds classes
   * imperatively, and React never touches attributes it doesn't render.
   */
  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return;
    const rows = Array.from(scope.querySelectorAll<HTMLElement>('.tugrow'));
    if (!rows.length) return;

    let queued = 0;
    const update = () => {
      queued = 0;
      // A row counts as reached once its top passes 62% of the way down the
      // screen — just below the middle, where the eye is while scrolling.
      const line = window.innerHeight * 0.62;
      let current: HTMLElement | null = null;
      for (const row of rows) {
        const reached = row.getBoundingClientRect().top < line;
        row.dataset.reached = reached ? 'true' : 'false';
        if (reached) current = row;
      }
      for (const row of rows) row.dataset.current = row === current ? 'true' : 'false';
    };
    const schedule = () => {
      if (!queued) queued = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(queued);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  return (
    <ElfShell>
      <div className="elf-page" ref={scopeRef}>
        <div className="elf-page__panel">
          <ElfPageNav current="/why-elf-circle" />
          <div style={{ padding: '18px var(--pgut) 0' }}>
            <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Why ELF-CIRCLE']} />
          </div>

          {/* --- 10a — the thesis --- */}
          <div className="thesis">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <span className="eyebrow" style={{ alignSelf: 'flex-start' }}>
                <i />
                Built from culture, not assigned to it
              </span>
              <h1 className="elf-page__h1 thesis__h1">
                The Multicultural Marketing &amp; Web-Development Agency
                <br />
                <span style={{ color: 'var(--acc-panel)' }}>Built From Culture — Not Assigned To It.</span>
              </h1>
              <p className="wbody" style={{ fontSize: 15, maxWidth: '60ch' }}>
                Most agencies treat multicultural marketing as a division looped in for a moment. Entertainment,
                Lifestyle and Fashion — as categories — <i>are</i> multicultural marketing. ELF-CIRCLE was built from
                culture, grassroots up, thirty-five years before &ldquo;multicultural&rdquo; became a line item on
                someone else&rsquo;s org chart.
              </p>
              <div className="thesis__stats">
                {STATS.map(([n, label]) => (
                  <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span className="thesis__stat">{n}</span>
                    <span className="wtag" style={{ fontSize: 11 }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="thesis__art">
              {/* One street, one board, two adverts: drag the bar to swap between
                  the firm and its build partner. The frame is portrait and the
                  photo landscape, so the crop is held on the board. */}
              <ElfShutter
                focus="63% 50%"
                left={{
                  src: billboardArt,
                  alt: 'An ELF-CIRCLE advertisement on a city billboard',
                  label: 'ELF-CIRCLE',
                }}
                right={{
                  src: voilacodeArt,
                  alt: 'A Voilacode advertisement on the same billboard',
                  label: 'Voilacode',
                }}
              />
            </div>
          </div>

          {/* --- 10b — head to head --- */}
          <div style={{ padding: '30px var(--pgut) 4px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="colhead" style={{ padding: '0 0 8px' }}>
              One roof vs. five vendors
            </span>
            {TUG.map(([dim, typical, elf]) => (
              <div
                className="tugrow elf-rise"
                key={dim}
                data-on={pulled === dim ? 'true' : undefined}
                role="button"
                tabIndex={0}
                aria-pressed={pulled === dim}
                onClick={() => setPulled((cur) => (cur === dim ? null : dim))}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  setPulled((cur) => (cur === dim ? null : dim));
                }}
              >
                <span className="tudim">{dim}</span>
                <div className="tutrack">
                  <span className="tuneedle typ">{typical}</span>
                  <span className="tuneedle own">{elf}</span>
                </div>
              </div>
            ))}
          </div>

          {/* --- 10c — the four pillars --- */}
          <div className="pillarband">
            <span className="pillarband__art">
              {/* One picture per pillar, all mounted; the current one is faded in. */}
              {PILLARS.map((p, i) => (
                <img key={p.num} src={p.art} alt="" data-on={i === pillar ? 'true' : undefined} />
              ))}
              <span />
            </span>
            <p className="pillarband__hint">
              The four pillars —{' '}
              <span className="hint-hover">hover to fan out</span>
              <span className="hint-touch">{fanned ? 'tap to stack them again' : 'tap to fan out'}</span>
            </p>
            <div
              className="stackdeck"
              data-fanned={fanned ? 'true' : undefined}
              role="button"
              tabIndex={0}
              aria-expanded={fanned}
              aria-label="The four pillars"
              onClick={() => setFanned((v) => !v)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                setFanned((v) => !v);
              }}
            >
              {PILLARS.map((p, i) => (
                <div
                  className={`stackcard sc${i + 1}`}
                  key={p.num}
                  data-on={i === pillar ? 'true' : undefined}
                  onPointerEnter={(e) => {
                    if (e.pointerType === 'mouse') setPillar(i);
                  }}
                  // Touch: once fanned, tapping a card picks its picture without
                  // also folding the deck back up.
                  onClick={(e) => {
                    if (!fanned) return;
                    e.stopPropagation();
                    setPillar(i);
                  }}
                >
                  <span className="pnum">{p.num}</span>
                  <span className="pname">{p.name}</span>
                  <span className="pmore">{p.more}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link className="nextlink" to={route('/portfolio')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Owned IP, first</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Made In New Jersey
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Leads the Portfolio hierarchy.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <button
              type="button"
              className="nextlink"
              onClick={() => consult?.open({ source: 'why-elf-circle.proposal', title: 'Request a proposal' })}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Start here</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Request a proposal
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Bring this case to your RFP.
                </span>
              </div>
              <span className="arw">→</span>
            </button>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default WhyElfCircle;
